import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type FactKind = 'stakeholder' | 'dependency';

export interface DetectedFact {
  id: string;
  item_key: string;
  dossier_key: string;
  payload: Record<string, unknown>;
}

/** A project's facts of one kind: waiting for review, and already approved. */
export interface ProjectFactGroup<TApproved> {
  projectId: string;
  projectName: string;
  dossierKey: string;
  detected: DetectedFact[];
  approved: TApproved[];
}

const TABLE = { stakeholder: 'project_stakeholders', dependency: 'project_dependencies' } as const;
const PAGE = 5000;

/**
 * Detected and approved facts of one kind (stakeholders or dependencies) for every
 * project, grouped by project. A dossier (a project's document set) is tied to its
 * project through document_candidates.
 */
export function useProjectFacts<TApproved extends { project_id: string }>(kind: FactKind) {
  const [groups, setGroups] = useState<Array<ProjectFactGroup<TApproved>>>([]);
  const [loading, setLoading] = useState(true);
  const [busyProject, setBusyProject] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [candRes, itemsRes, approvedRes] = await Promise.all([
      supabase.from('document_candidates').select('dossier_key, matched_project_id').not('dossier_key', 'is', null).not('matched_project_id', 'is', null).range(0, PAGE - 1),
      supabase.from('project_intel_items').select('id, item_key, dossier_key, payload').eq('kind', kind).eq('status', 'suggested').order('created_at').range(0, PAGE - 1),
      supabase.from(TABLE[kind]).select('*').order('created_at').range(0, PAGE - 1),
    ]);
    const dossierProject = new Map<string, string>();
    for (const c of candRes.data ?? []) if (c.dossier_key && c.matched_project_id) dossierProject.set(c.dossier_key, c.matched_project_id);
    const detected = ((itemsRes.data ?? []) as unknown) as DetectedFact[];
    const approved = ((approvedRes.data ?? []) as unknown) as TApproved[];

    const projectIds = [...new Set([...dossierProject.values(), ...approved.map((a) => a.project_id)])];
    const names = new Map<string, string>();
    if (projectIds.length > 0) {
      const { data } = await supabase.from('projects_sync').select('id, name').in('id', projectIds.slice(0, 1000));
      for (const p of data ?? []) names.set(p.id, p.name);
    }

    const byProject = new Map<string, ProjectFactGroup<TApproved>>();
    const group = (projectId: string, dossierKey: string) => {
      let g = byProject.get(projectId);
      if (!g) {
        g = { projectId, projectName: names.get(projectId) ?? 'Unnamed project', dossierKey, detected: [], approved: [] };
        byProject.set(projectId, g);
      }
      if (!g.dossierKey) g.dossierKey = dossierKey;
      return g;
    };
    const projectDossier = new Map([...dossierProject].map(([d, p]) => [p, d]));
    for (const d of detected) {
      const projectId = dossierProject.get(d.dossier_key);
      if (projectId) group(projectId, d.dossier_key).detected.push(d);
    }
    for (const a of approved) group(a.project_id, projectDossier.get(a.project_id) ?? '').approved.push(a);

    setGroups([...byProject.values()].sort((a, b) => a.projectName.localeCompare(b.projectName)));
    setLoading(false);
  }, [kind]);

  useEffect(() => { void load(); }, [load]);

  const approve = useCallback(async (g: ProjectFactGroup<TApproved>, excludedIds: string[]) => {
    setBusyProject(g.projectId);
    setError(null);
    const { data, error: rpcError } = await supabase.rpc('approve_project_intel', {
      p_project_id: g.projectId,
      p_dossier_key: g.dossierKey,
      p_reject: excludedIds,
      p_kinds: [kind],
    });
    if (rpcError) { setBusyProject(null); setError(rpcError.message); return null; }
    // Refresh signals from the new facts. Best effort: roles outside admin/PMO/executive are refused, which is fine.
    void supabase.functions.invoke('generate-signals', { body: { action: 'scan' } });
    await load();
    setBusyProject(null);
    return data as { approved: number; rejected: number; remaining: number };
  }, [kind, load]);

  const undo = useCallback(async (g: ProjectFactGroup<TApproved>) => {
    setBusyProject(g.projectId);
    setError(null);
    const { data, error: rpcError } = await supabase.rpc('undo_project_intel', { p_dossier_key: g.dossierKey, p_kinds: [kind] });
    if (rpcError) { setBusyProject(null); setError(rpcError.message); return null; }
    void supabase.functions.invoke('generate-signals', { body: { action: 'scan' } });
    await load();
    setBusyProject(null);
    return data as { reverted: number; skipped: Array<{ id: string; title: string }> };
  }, [kind, load]);

  /** Add every project's detected facts of this kind at once. */
  const approveAll = useCallback(async () => {
    setBusyProject('*');
    setError(null);
    const { data, error: rpcError } = await supabase.rpc('approve_all_project_intel', { p_kinds: [kind] });
    if (rpcError) { setBusyProject(null); setError(rpcError.message); return null; }
    void supabase.functions.invoke('generate-signals', { body: { action: 'scan' } });
    await load();
    setBusyProject(null);
    return data as { approved: number; projects: number };
  }, [kind, load]);

  const undoAll = useCallback(async () => {
    setBusyProject('*');
    setError(null);
    const { data, error: rpcError } = await supabase.rpc('undo_all_project_intel', { p_kinds: [kind] });
    if (rpcError) { setBusyProject(null); setError(rpcError.message); return null; }
    void supabase.functions.invoke('generate-signals', { body: { action: 'scan' } });
    await load();
    setBusyProject(null);
    return data as { reverted: number; skipped: number };
  }, [kind, load]);

  const totals = useMemo(() => ({
    detected: groups.reduce((n, g) => n + g.detected.length, 0),
    approved: groups.reduce((n, g) => n + g.approved.length, 0),
  }), [groups]);

  return { groups, totals, loading, busyProject, error, approve, undo, approveAll, undoAll, reload: load };
}
